import { useEffect, useRef } from 'react';
import { connectOneSignal, disconnectOneSignal } from '../services/oneSignal';
import { useAuth } from '../context/AuthContext';

interface Props {
  children: React.ReactNode;
}

export default function OneSignalProvider({ children }: Props) {
  const { user, isLogged } = useAuth();
  const hasInitialized = useRef(false);
  const lastUserId = useRef<string | null>(null);
  const lastUserData = useRef<string | null>(null);

  useEffect(() => {
    // Aguarda até que o estado de login seja definido (não mais undefined)
    const loginState = isLogged();
    if (loginState === undefined) {
      console.log('OneSignal: Aguardando definição do estado de login...');
      return;
    }

    const currentUserId = user?.id || null;
    
    // Cria hash dos dados relevantes para detectar mudanças
    const currentUserData = currentUserId 
      ? JSON.stringify({
          id: user.id,
          username: user.username,
          vehicles: user.vehicles?.map((v: any) => v.uvId).sort(),
          userTags: user.userTags,
        })
      : null;
    
    // Na primeira inicialização, envia apenas um sinal após determinar o estado
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      lastUserId.current = currentUserId;
      lastUserData.current = currentUserData;
      console.log('OneSignal: Inicialização -', currentUserId ? 'LOGIN' : 'LOGOUT', { userId: currentUserId });
      
      setTimeout(() => {
        if (currentUserId && user) {
          connectOneSignal(user);
        } else {
          disconnectOneSignal();
        }
      }, 1000);
      return;
    }

    // Mudança de logout (user tinha ID, agora não tem)
    if (lastUserId.current && !currentUserId) {
      lastUserId.current = null;
      lastUserData.current = null;
      console.log('OneSignal: LOGOUT');
      
      setTimeout(() => {
        disconnectOneSignal();
      }, 1000);
      return;
    }

    // Mudança de login (user não tinha ID, agora tem)
    if (!lastUserId.current && currentUserId) {
      lastUserId.current = currentUserId;
      lastUserData.current = currentUserData;
      console.log('OneSignal: LOGIN', { userId: currentUserId });
      
      setTimeout(() => {
        connectOneSignal(user);
      }, 1000);
      return;
    }

    // Usuário logado - verifica se dados relevantes mudaram
    if (currentUserId && currentUserData !== lastUserData.current) {
      lastUserData.current = currentUserData;
      console.log('OneSignal: Atualizando dados do usuário (tags/veículos)', { userId: currentUserId });
      
      setTimeout(() => {
        connectOneSignal(user);
      }, 100);
      return;
    }

  }, [user, isLogged]);

  return <>{children}</>;
}
